interface BarChartProps {
  data: Array<{
    label?: string
    value?: number
  }>
  maxValue?: number
  highlightIndex?: number
}

export function BarChart({ data, maxValue, highlightIndex = 2 }: BarChartProps) {
  const safeData = Array.isArray(data)
    ? data.map((d, idx) => ({
        label: d?.label ?? `Item ${idx + 1}`,
        value: typeof d?.value === 'number' ? d.value : 0,
      }))
    : []

  const max = maxValue || Math.max(1, ...safeData.map((d) => d.value))

  // Y-axis values
  const yAxisLabels = [50, 40, 30, 20, 10, 0]

  // Format value for display (e.g., 45000 -> 45k)
  const formatValue = (value: number) => {
    if (value >= 1000) {
      return `${Math.round(value / 1000)}k`
    }
    return String(value)
  }

  return (
    <div className='relative h-full min-h-[280px] sm:min-h-[400px] flex'>
      {/* Y-axis labels */}
      <div className='flex flex-col justify-between text-[14px] text-[#434343] font-normal pr-4 py-6'>
        {yAxisLabels.map((label, idx) => (
          <span key={idx} className='leading-6 text-right'>{label}</span>
        ))}
      </div>

      {/* Chart area */}
      <div className='flex-1 relative'>
        {/* Bars container */}
        <div className='absolute inset-0 flex items-end pb-12 pt-6'>
          <div className='flex items-end justify-between w-full h-full gap-2'>
            {safeData.map((item, index) => {
              const heightPercent = max > 0 ? (item.value / max) * 100 : 0
              const isHighlighted = index === highlightIndex

              return (
                <div key={index} className='flex-1 flex flex-col items-center h-full justify-end'>
                  {/* Value label */}
                  <div className='text-[15px] font-medium text-[#151515] mb-2 leading-[22px]'>
                    {formatValue(item.value)}
                  </div>
                  {/* Bar */}
                  <div
                    className={`w-7 rounded-t-md transition-all ${
                      isHighlighted
                        ? 'bg-gradient-to-r from-[#B2B8C4] to-[#B2B8C4]'
                        : 'bg-[#edeff3]'
                    }`}
                    style={{
                      height: `${Math.max(heightPercent, 2)}%`,
                      background: isHighlighted
                        ? 'linear-gradient(90deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), linear-gradient(90deg, rgb(178, 184, 196) 0%, rgb(178, 184, 196) 100%)'
                        : '#edeff3'
                    }}
                  />
                </div>
              )
            })}
          </div>
        </div>

        {/* Bottom line */}
        <div className='absolute bottom-12 left-0 right-0 h-[1px] bg-[#DBDADE]' />

        {/* X-axis labels */}
        <div className='absolute bottom-0 left-0 right-0 flex justify-between'>
          {safeData.map((item, index) => (
            <div key={index} className='flex-1 text-center min-w-0 px-0.5'>
              <span className='text-[10px] sm:text-[12px] lg:text-[14px] text-[#434343] font-normal leading-4 block truncate'>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
